import { gapPageTitle } from '@/components/NewUI/Components/SingleRootGap/gapDetails';

import { locationDocument } from './documents';
import { admittedRouteFor, staticDataOf } from './navigation';
import { recordNamesAt } from './recordNames';

import type { AnyRouter } from '@tanstack/react-router';

import type { RouteTitleContext } from './types';

export const gapDocumentTitle = (pageTitle: string, railsAction: string): string =>
  `Not built · ${gapPageTitle(pageTitle, railsAction)}`;

// The admin layout's `page_title_head` (application.page_title.admin.one).
const ADMIN_TITLE_PREFIX = ':Recruit - ';
export const adminDocumentTitle = (title: string): string => `${ADMIN_TITLE_PREFIX}${title}`;

/**
 * Title template for a record page: the record's name, as its HAML page titles it, once the
 * served document or the route's data names it; `fallback` until then.
 */
export const recordTitle =
  (fallback: string) =>
  ({ pageTitle, recordName, servedDocument }: RouteTitleContext): string => {
    const name = servedDocument && pageTitle !== '' ? pageTitle : recordName;
    return adminDocumentTitle(name === null || name === '' ? fallback : name);
  };

// Title for the resolved location: an admitted route's template, else the gap title for the
// server's document there. A location still being probed keeps the previous title.
export const resolveDocumentTitle = (router: AnyRouter): string | null => {
  const { location } = router.state;
  // The server's page title describes its own location only, never a later one.
  const serverDocument = locationDocument(router);
  const route = admittedRouteFor(router, location.pathname);
  if (route) {
    const { title } = staticDataOf(route);
    if (typeof title !== 'function') return title ?? null;
    return title({
      servedDocument: serverDocument !== null,
      pageTitle: serverDocument?.pageTitle ?? '',
      recordName: recordNamesAt(router, location.pathname).recordName
    });
  }
  return serverDocument
    ? gapDocumentTitle(serverDocument.pageTitle, serverDocument.railsAction)
    : null;
};

export const applyDocumentTitle = (router: AnyRouter): void => {
  const title = resolveDocumentTitle(router);
  if (title) document.title = title;
};

// The page's name for a one-crumb trail: its title without the document title's template.
export const resolvePageName = (router: AnyRouter): string | null => {
  if (!admittedRouteFor(router, router.state.location.pathname)) {
    const serverDocument = locationDocument(router);
    return serverDocument
      ? gapPageTitle(serverDocument.pageTitle, serverDocument.railsAction)
      : null;
  }
  const title = resolveDocumentTitle(router);
  return title?.startsWith(ADMIN_TITLE_PREFIX) ? title.slice(ADMIN_TITLE_PREFIX.length) : title;
};
